/** Home is a fast way into the work: Ask, common actions, and recent charts. */

import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  activeSection,
  activeTab,
  isRootUser,
  myPermissions,
  type Section,
  type Tab,
} from '../../state/store.ts';
import { authState } from '../../auth/auth.ts';
import { isPageVisible, pageVisibility } from '../../state/page-visibility.ts';
import { openAssistantDrawer } from '../../state/assistant-surface.ts';
import { recentViews, removeRecent, type RecentView } from '../../state/recents.ts';
import { restoreFromUrl, syncToUrl } from '../../state/url-sync.ts';
import { doPlot } from '../plot/plot-actions.ts';
import { resizePlot } from '../plot/render/chart-dom.ts';
import { plotGlyph, seriesSummary } from '../catalog/chart-summary.ts';
import { assistantMark } from '../assistant/mark.ts';
import { shipLabel } from '../../utils/ships.ts';
import { timeAgo } from '../../utils/dates.ts';

const RECENTS_SHOWN = 4;

const PLOT_GLYPH = svg`<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path d="M3.5 18.5h17M4.5 16l4-5 3.2 2.7 4.1-7.2 3.7 3.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
const EXPORT_GLYPH = svg`<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path d="M12 3.5v11m-4-4 4 4 4-4M4 17.5v2h16v-2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
const MODEL_GLYPH = svg`<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path d="M4 19.5V4.5m0 15h16M6.5 16.5c2.1-6.7 4.4-7.5 6.1-4.2 1.7 3.3 3.3 1.7 5.4-5.1" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
const DASHBOARD_GLYPH = svg`<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <rect x="3.5" y="4" width="7" height="6" rx=".7" stroke="currentColor" stroke-width="1.6"/>
  <rect x="13.5" y="4" width="7" height="10" rx=".7" stroke="currentColor" stroke-width="1.6"/>
  <rect x="3.5" y="13" width="7" height="7" rx=".7" stroke="currentColor" stroke-width="1.6"/>
  <rect x="13.5" y="17" width="7" height="3" rx=".7" stroke="currentColor" stroke-width="1.6"/>
</svg>`;

function greeting(now: Date): string {
  const hour = now.getHours();
  if (hour < 5) return 'Still up';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function firstName(name: string | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] ?? '';
}

@customElement('home-page')
export class HomePage extends LightElement {
  private store = new StoreController(this, [
    activeSection,
    recentViews,
    authState,
    pageVisibility,
    isRootUser,
    myPermissions,
  ]);

  private go(section: Section, tab?: Tab): void {
    if (tab) {
      activeTab.set(tab);
      document.body.dataset.activeTab = tab;
    }
    activeSection.set(section);
    document.body.dataset.section = section;
    syncToUrl();
    if (section === 'explore') requestAnimationFrame(() => resizePlot());
  }

  private async openRecent(view: RecentView): Promise<void> {
    this.go('explore', 'plot');
    await restoreFromUrl(`?${view.q}`);
    void doPlot();
  }

  private recentTitle(view: RecentView): string {
    const params = Object.fromEntries(new URLSearchParams(view.q).entries());
    const series = seriesSummary({ ...params, pt: view.plotType });
    const who = view.ships.length > 1
      ? `${view.ships.length} ships`
      : shipLabel(view.ships[0] ?? '');
    return who ? `${series} · ${who}` : series;
  }

  private renderRecents(): TemplateResult | typeof nothing {
    const recents = recentViews.value.slice(0, RECENTS_SHOWN);
    if (!recents.length) return nothing;
    return html`
      <section class="home-block">
        <h3 class="home-block-head">Pick up where you left off</h3>
        <ul class="home-cards">
          ${recents.map((view) => html`
            <li class="home-card">
              <button class="home-card-open" @click=${() => void this.openRecent(view)}>
                <span class="home-card-shot">
                  ${view.thumb
                    ? html`<img src=${view.thumb} alt="" loading="lazy" />`
                    : html`<span class="home-card-glyph">${plotGlyph(view.plotType)}</span>`}
                </span>
                <span class="home-card-title">${this.recentTitle(view)}</span>
                <span class="home-card-meta">${view.plotType} · ${timeAgo(new Date(view.ts).toISOString())}</span>
              </button>
              <button class="home-card-forget" title="Forget this view" aria-label="Forget this view"
                @click=${() => removeRecent(view.q)}>×</button>
            </li>
          `)}
        </ul>
      </section>
    `;
  }

  private renderActions(): TemplateResult {
    const actions: Array<{
      title: string;
      sub: string;
      glyph: TemplateResult;
      primary?: boolean;
      run: () => void;
    }> = [];
    if (isPageVisible('plot')) {
      actions.push({
        title: 'Plot a signal',
        sub: 'Compare ships, signals, and periods',
        glyph: PLOT_GLYPH,
        primary: true,
        run: () => this.go('explore', 'plot'),
      });
    }
    if (isPageVisible('export')) {
      actions.push({
        title: 'Export data',
        sub: 'CSV, Parquet or Excel',
        glyph: EXPORT_GLYPH,
        run: () => this.go('explore', 'export'),
      });
    }
    if (isPageVisible('modelviewer')) {
      actions.push({
        title: 'Model Viewer',
        sub: 'Explore power and SFOC curves',
        glyph: MODEL_GLYPH,
        run: () => this.go('explore', 'modelviewer'),
      });
    }
    if (isPageVisible('dashboards')) {
      actions.push({
        title: 'Build a dashboard',
        sub: 'Bring charts together on one page',
        glyph: DASHBOARD_GLYPH,
        run: () => this.go('dashboards'),
      });
    }
    return html`
      <section class="home-block">
        <h3 class="home-block-head">What you can do</h3>
        <ul class="home-actions">
          ${actions.map((action) => html`
            <li>
              <button class="home-action ${action.primary ? 'is-primary' : ''}" @click=${action.run}>
                <span class="home-action-icon">${action.glyph}</span>
                <span class="home-action-copy">
                  <strong>${action.title}</strong>
                  <span>${action.sub}</span>
                </span>
                <span class="home-action-arrow" aria-hidden="true">→</span>
              </button>
            </li>
          `)}
        </ul>
      </section>
    `;
  }

  override render(): TemplateResult {
    const name = firstName(authState.value.user?.name);
    return html`
      <div class="home scroll-thin">
        <header class="home-head">
          <h2 class="home-greet">
            ${greeting(new Date())}${name ? html`, <strong>${name}</strong>` : nothing}
          </h2>
          <p class="home-lead">Where would you like to start?</p>
        </header>

        ${isPageVisible('assistant') ? html`
          <button class="home-assistant" @click=${openAssistantDrawer}>
            <span class="home-assistant-mark">${assistantMark(25)}</span>
            <span class="home-assistant-copy">
              <strong>Ask SONAR</strong>
              <span>Ask about ships and signals, or describe the chart you need.</span>
            </span>
            <span class="home-assistant-go" aria-hidden="true">Open chat <span>↗</span></span>
          </button>
        ` : nothing}

        ${this.renderActions()}
        ${this.renderRecents()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'home-page': HomePage;
  }
}
